import { useDebounce } from '@hooks/useDebounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type SortKey<T> = Extract<keyof T, string> | `-${Extract<keyof T, string>}`;

interface UsePaginationProps<T> {
    allowedColumns?: (keyof T)[];
    defaultSort: SortKey<T>;
}

const usePagination = <T>({ allowedColumns, defaultSort }: UsePaginationProps<T>) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const sortParam = useMemo(() => searchParams.get('sort'), [searchParams]);
    const page = useMemo(() => searchParams.get('page'), [searchParams]);
    const searchParam = useMemo(() => searchParams.get('search'), [searchParams]);
    const pageSizeParam = useMemo(() => searchParams.get('page_size'), [searchParams]);

    const filters: Record<'sort' | 'page' | 'search' | 'page_size', string> = useMemo(
        () => ({
            sort: allowedColumns
                ? allowedColumns.includes(sortParam?.replace('-', '') as keyof T)
                    ? sortParam ?? ''
                    : defaultSort
                : defaultSort,
            page: Number.isNaN(page) || !page ? '1' : page ?? '1',
            page_size: Number.isNaN(pageSizeParam) || !pageSizeParam ? '10' : pageSizeParam ?? '10',
            search: searchParam ?? '',
        }),
        [allowedColumns, defaultSort, page, pageSizeParam, searchParam, sortParam]
    );
    const sort = useMemo(
        () => ({
            column: ((filters.sort as string) || undefined)?.replace('-', '') as keyof T,
            direction: ((filters.sort as string) || undefined)?.includes('-') ? 'desc' : ('asc' as 'desc' | 'asc'),
        }),
        [filters.sort]
    );
    const [search, setSearch] = useState(filters.search);

    const handleParamChange = useCallback(
        (field: string, value: string | string[] | null, isDelete?: boolean) => {
            if (isDelete) {
                const values = searchParams.get(field);

                if (values?.split(',').length === 1) {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.set('page', '1');
                        params.delete(field);
                        return params;
                    });
                } else {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.set('page', '1');
                        params.set(
                            field,
                            values
                                ?.split(',')
                                .filter((x) => x !== value)
                                .join(',') ?? ''
                        );
                        return params;
                    });
                }
            } else if (searchParams.get(field) !== value) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    if (value) {
                        params.set(field, typeof value === 'string' ? value : value.join(','));
                    } else {
                        params.delete(field);
                    }
                    return params;
                });
            }
        },
        [searchParams, setSearchParams]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('page', `${newPage}`);
                return params;
            });
        },
        [setSearchParams]
    );

    const handlePageSizeChange = useCallback(
        (newPageSize: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('page_size', `${newPageSize}`);
                return params;
            });
        },
        [setSearchParams]
    );

    const handleSortClick = useCallback(
        (column: Extract<keyof T, string>) => {
            const isDesc = sort.column === column && sort.direction === 'desc';
            handleParamChange('sort', `${!isDesc ? '-' : ''}${column}`);
        },
        [sort.column, sort.direction, handleParamChange]
    );

    useDebounce({
        value: search,
        delay: 500,
        customHandler: (value) => handleParamChange('search', value),
        trigger: () => (searchParam ?? '') !== search,
    });
    useEffect(() => {
        setSearch(filters.search as string);
    }, [filters.search, setSearchParams]);

    return {
        filters,
        sort,
        handleParamChange,
        handlePageChange,
        handleSortClick,
        handlePageSizeChange,
        search,
        setSearch,
        page,
    };
};

export { usePagination };
